import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_SUPPLIER,
  ROUTE_SUPPLIERS
} from '@klear/constants'
import { KlearDate } from '@klear/date'
import { useSuppliers, useSupplierMutations } from '@klear/ui/hooks/use-suppliers'
import { useTraders } from '@klear/ui/hooks/use-traders'
import constant from 'lodash/constant'

import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams, GridReadyEvent, GridApi, ValueGetterParams } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import ViewIcon from '@/icons/ViewIcon'
import EditIcon from '@/icons/EditIcon'
import DeleteIcon from '@/icons/DeleteIcon'
import AddIcon from '@/icons/AddIcon'
import ExportIcon from '@/icons/ExportIcon'
import { DeleteButton } from '@klear/ui/components/delete-button'
import { Button } from '@klear/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const Suppliers = () => {
  useDocumentTitle('Suppliers')
  const { user } = useAuth()
  const [gridApi, setGridApi] = useState<GridApi | null>(null)

  const { suppliers, isLoading, error } = useSuppliers()
  const { traders } = useTraders()
  const { deleteSupplier, isDeleting } = useSupplierMutations()
  const agGrid = useKlearAgGrid()

  const traderNameById = useMemo(() => new Map((traders ?? []).map((t) => [t.id, t.name ?? ''])), [traders])

  const ActionCellRenderer = useCallback(
    (props: ICellRendererParams) => {
      const supplier = props.data
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/${ROUTE_SUPPLIERS}/${supplier.id}`} className="flex items-center gap-2">
                <ViewIcon />
                View
              </Link>
            </DropdownMenuItem>
            {user.hasPermission(PERMISSION_ENTITY_SUPPLIER, PERMISSION_ACTION_EDIT) && (
              <DropdownMenuItem asChild>
                <Link to={`/${ROUTE_SUPPLIERS}/${supplier.id}/edit`} className="flex items-center gap-2">
                  <EditIcon />
                  Edit
                </Link>
              </DropdownMenuItem>
            )}
            {user.hasPermission(PERMISSION_ENTITY_SUPPLIER, PERMISSION_ACTION_DELETE) && (
              <DeleteButton
                resourceId={supplier.id}
                resourceName={supplier.name}
                resourceType={PERMISSION_ENTITY_SUPPLIER}
                deleteMutation={(params: { id: number }) =>
                  deleteSupplier({
                    id: params.id,
                    traderId: supplier.traderId
                  })
                }
                isDeleting={isDeleting}
                wrapperComponent={DropdownMenuItem}
              >
                <span className="flex items-center gap-2 text-red-600">
                  <DeleteIcon />
                  Delete
                </span>
              </DeleteButton>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    [user, deleteSupplier, isDeleting]
  )

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Organization Name',
        field: 'name',
        pinned: 'left',
        lockPosition: true,
        minWidth: 200,
        colId: 'name'
      },
      {
        headerName: 'Trader',
        colId: 'traderName',
        type: 'text',
        valueGetter: (params: ValueGetterParams) => traderNameById.get(params.data?.traderId) ?? ''
      },
      {
        headerName: 'Country',
        field: 'country'
      },
      {
        headerName: 'Created Date',
        field: 'createdAt',
        type: 'klearDate'
      },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        cellRenderer: ActionCellRenderer,
        pinned: 'right',
        lockPosition: true,
        menuTabs: [],
        valueGetter: constant(null),
        cellStyle: { overflow: 'visible' },
        flex: 0,
        width: 60,
        minWidth: 60,
        maxWidth: 60
      }
    ],
    [ActionCellRenderer, traderNameById]
  )

  const defaultColDef = useMemo(
    () => ({
      ...agGrid.defaultColDef,
      flex: 1,
      minWidth: 150
    }),
    [agGrid.defaultColDef]
  )

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  const onFilterTextBoxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', e.target.value)
    }
  }

  const handleExport = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: `suppliers_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: ['name', 'country', 'createdAt']
      })
    }
  }

  useEffect(() => {
    if (!gridApi) return
    if (isLoading) {
      gridApi.showLoadingOverlay()
      return
    }
    if (suppliers && suppliers.length > 0) {
      gridApi.hideOverlay()
    } else {
      gridApi.showNoRowsOverlay()
    }
  }, [gridApi, isLoading, suppliers])

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">Error: {error.message}</div>
  }

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Suppliers</h1>
        {user.hasPermission(PERMISSION_ENTITY_SUPPLIER, PERMISSION_ACTION_ADD) && (
          <Link
            to={`/${ROUTE_SUPPLIERS}/new`}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
          >
            <AddIcon />
            <span>Create Supplier</span>
          </Link>
        )}
      </div>

      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search suppliers by name"
          onChange={onFilterTextBoxChanged}
          className="flex-grow px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
          disabled={!gridApi || !suppliers || suppliers.length === 0}
        >
          <ExportIcon />
          <span>Export CSV</span>
        </button>
      </div>

      {!suppliers || suppliers.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No suppliers found matching your search criteria.</p>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="w-full h-full min-h-[600px]">
            <AgGridReact
              rowData={suppliers}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              columnTypes={agGrid.columnTypes}
              dataTypeDefinitions={agGrid.dataTypeDefinitions}
              animateRows={true}
              rowSelection="multiple"
              pagination={true}
              paginationPageSize={20}
              theme={agGrid.theme}
              onGridReady={onGridReady}
              quickFilterText=""
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Suppliers
